import type { Ref } from 'vue'
import { shallowReadonly, shallowRef, watch } from 'vue'
import type { TextLabel } from '@/lib/three-d-resources/labels-manager'
import type { World } from '@/lib/three-d-resources/world'
import { getLayerColour } from '@/lib/three-d-resources/layer-utils'
import { DEFAULT_CATEGORY } from '~/lib/three-d-viewer/layer-utils'
import { makeEscapedRegExp } from '~/lib/utils'
import { isTextLabel } from '~/lib/three-d-editor/utils'

export type Layer = {
  title: string
  nodeId: number
  nodeName: string
  layerNumber: number
  color: string
  visible: boolean
}

export type LabelLayer = Layer | TextLabel

export interface LabelLayerGroup {
  type: 'layer'
  name: string
  id: number
  layers: LabelLayer[]
  isDefault: boolean
  layerNumber: number
  allVisible: boolean
  someVisible: boolean
}

export function useThreeDLayerGroups({ world, search }: { world: World; search: Ref<string> }) {
  const layerGroups = shallowRef<LabelLayerGroup[]>([])

  watch(search, rebuildLayerGroups)

  function clearLayerGroups() {
    layerGroups.value = []
  }

  function rebuildLayerGroups() {
    if (!world.currentModel) {
      layerGroups.value = []
      return
    }

    layerGroups.value = Array.from(world.currentModel.apiLayersById.values())
      .reduce<LabelLayerGroup[]>((acc, layer) => {
        const categoryNode = world.currentModel!.apiCategoriesById.get(layer.parent_id!)
        const category = categoryNode?.category ?? DEFAULT_CATEGORY
        const searchFilters = search.value.split(' ').map(makeEscapedRegExp)

        let layerGroup = acc.find(cat => cat.name === category)
        if (!layerGroup) {
          const labels = (world.labelsManager?.getLabelsForNode(categoryNode?.id ?? 0) ?? []).filter(label =>
            searchFilters.every(x => x.test(label.content))
          )

          layerGroup = {
            type: 'layer',
            name: category,
            id: categoryNode?.id ?? 0,
            isDefault: category === DEFAULT_CATEGORY,
            layers: [...labels],
            layerNumber: categoryNode?.layer ?? Infinity,
            someVisible: true,
            allVisible: true,
          }
          acc.push(layerGroup)
        }

        const sceneNode = world.currentModel!.layerNodesById.get(layer.id)

        // search matches layer title or search is blank
        const isLayerMatch = searchFilters.every(x => x.test(layer.display_name))
        const labels = (world.labelsManager?.getLabelsForNode(layer.id) ?? []).filter(label =>
          searchFilters.every(x => x.test(label.content))
        )

        if (!search.value) {
          layerGroup.layers.push(
            {
              nodeId: layer.id,
              nodeName: layer.node_name,
              title: layer.display_name,
              layerNumber: layer.layer,
              color: getLayerColour(sceneNode, world.currentModel!.apiLayersById),
              visible: world.isNodeVisible(layer.id),
            },
            ...labels
          )
        } else if (isLayerMatch) {
          layerGroup.layers.push({
            nodeId: layer.id,
            nodeName: layer.node_name,
            title: layer.display_name,
            layerNumber: layer.layer,
            color: getLayerColour(sceneNode, world.currentModel!.apiLayersById),
            visible: world.isNodeVisible(layer.id),
          })
        } else {
          layerGroup.layers.push(...labels)
        }

        return acc
      }, [])
      .map(group => ({
        ...group,
        someVisible: group.layers.some(l => l.visible),
        allVisible: group.layers.every(l => l.visible),
        layers: group.layers.sort((a: LabelLayer, b: LabelLayer) => (isTextLabel(a) ? 1 : 0) - (isTextLabel(b) ? 1 : 0)),
      }))
      .filter(group => group.layers.length > 0)
      .sort((a, b) => a.layerNumber - b.layerNumber)
  }

  return {
    layerGroups: shallowReadonly(layerGroups),
    clearLayerGroups,
    rebuildLayerGroups,
  }
}
